import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import BeforeAfter from './BeforeAfter';

// Text proposals, including older ones kept on remark documents, are judged
// here. Polling refreshes their state without clearing an action's conflict.
export default function TurnTextResults({ questId, locationUuid, field, turnIndex, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const seq = useRef(0);
  const load = useCallback(async () => {
    const n = ++seq.current;
    try { const r = await api.turnTextResults(questId, { locationUuid, field, turn: turnIndex }); if (n === seq.current) setData(r); }
    catch (e) { if (n === seq.current) setError(e.message); }
  }, [questId, locationUuid, field, turnIndex]);
  const invalidate = useCallback(() => { seq.current += 1; }, []);
  useEffect(() => {
    load();
    let timer;
    const schedule = () => { clearTimeout(timer); timer = setTimeout(load, 200); };
    for (const e of ['qv:tasks', 'qv:resync', 'qv:poll']) window.addEventListener(e, schedule);
    return () => { invalidate(); clearTimeout(timer); for (const e of ['qv:tasks', 'qv:resync', 'qv:poll']) window.removeEventListener(e, schedule); };
  }, [load, invalidate]);
  const act = async (r, reject) => {
    setBusy(r.runId); setError(null);
    try {
      const extra = { turnTaskId: data.turnTaskId };
      const result = reject ? await api.reject(r.taskId, r.runId, 'Text proposal declined in the turn.', false, extra) : await api.accept(r.taskId, r.runId, r.pendingTargetIds, false, extra);
      if (result.conflicts && result.conflicts.length) setError(result.conflicts.map((c) => c.reason).join('\n'));
      await load();
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e.data && e.data.conflicts ? e.data.conflicts.map((c) => c.reason).join('\n') : e.message);
      await load();
    } finally { setBusy(null); }
  };
  return (
    <div id="turn-text-results">
      {error ? <div className="tk-error" role="alert">{error} <button type="button" className="tk-linkbtn" onClick={() => setError(null)}>Dismiss message</button></div> : null}
      {(data ? data.results : []).map((r) => (
        <section key={r.runId} className="tk-result">
          <h3>Text proposal · {r.title}</h3>
          {r.outputs.map((o) => <div key={`${o.targetId}/${o.field}`}><span className="tk-muted">{o.field} · {o.state}</span><BeforeAfter output={o} questId={questId} /></div>)}
          {r.conflicts.length ? <div className="tk-error" role="alert">Could not apply: {[...new Set(r.conflicts.map((c) => c.reason))].join('; ')}. Ask the agent here to update the proposal against the current text, or reject it.</div> : null}
          <div className="tk-row">
            <button type="button" className="tk-btn tk-btn-primary" disabled={!!busy} onClick={() => act(r, false)}>Accept text changes</button>
            <button type="button" className="tk-btn tk-btn-ghost" disabled={!!busy} onClick={() => act(r, true)}>Reject text proposal</button>
          </div>
          <div className="tk-muted">The decision stays in this turn. The agent continues your earlier request here.</div>
        </section>
      ))}
    </div>
  );
}
